import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { OpenLanesContext } from '../lib/openLanes'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDismiss } from '../lib/useDismiss'
import { useHideOnScroll } from '../lib/useHideOnScroll'
import logo from '../assets/conetec-logo.svg'
import {
  BellIcon,
  InstallIcon,
  ConeTecMark,
  HomeIcon,
  QuestionIcon,
  StarIcon,
  PeopleIcon,
  PencilIcon,
  ShopIcon,
  FlagIcon,
  InboxIcon,
  ChartIcon,
  PulseIcon,
  ChevronIcon,
  CameraIcon,
  ImageIcon,
  TrophyIcon,
  SlidersIcon,
  InfoIcon,
  HistoryIcon, SurveyIcon, HelpIcon } from './icons'
import { useInstallPrompt } from '../lib/useInstallPrompt'
import { IdentityMenu, type ModeOption, type IdentityLink } from './IdentityMenu'
import { TabBar } from './TabBar'
import { AskOneDrawer } from './AskOneDrawer'
import { GuideSheet } from './guides/GuideSheet'
import { guideSheetSearch, guideSheetTarget, loadGuides, readableGuideSections, ReadableGuidesContext, resolveGuideSheet, type GuideRow } from '../lib/guides'
import { useAskOneSession } from './AskOneSession'
import { loadSupportLanes, type SupportLane } from '../lib/support'
import { WelcomeModal } from './WelcomeModal'
import { Tour } from './Tour'
import { loadTourSeen, markTourSeen, requestTour, takeTourRequest, TOUR_NAV } from '../lib/tour'
import { loadWelcomeModal, dismissNotice, type DbNotice } from '../lib/notices'
import { EnvironmentBanner } from './EnvironmentBanner'
import { loadEnvironmentName, type EnvironmentName } from '../lib/environment'
import { loadHelpQuestions, pageKeyFor, startersFor } from '../lib/askOnePage'
import { useHubLang, htmlLangFor } from '../lib/i18n'
import { SHELL, navLabel, headingLabel } from '../lib/hubCopy'
import { StarPulse } from './StarPulse'
import { BusinessCardDialog } from './BusinessCard'
import { BusinessCardContext, CARD_RELEASED } from '../lib/businessCard'
import { AdminDestinationsContext, ADMIN_HOME_ROUTE, adminBadge, adminDestinationCount, type AdminGroup } from '../lib/adminHome'
import { loadWaiting, waitingFor, onRefreshWaiting, refreshWaiting, badgeText, NOTHING_WAITING, type Waiting } from '../lib/waiting'
import { isAskOneKey, askOneKeyLabel } from '../lib/askOneKey'
import {
  describeNotification,
  loadNotifications,
  markNotificationRead,
  markNotificationUnread,
  notificationRoute,
  markAllNotificationsRead,
  readBellView,
  saveBellView,
  BELL_LIMIT,
  type BellView,
  type Notification,
} from '../lib/moderation'

/** The Ask One launcher's key, labelled for this platform once per page load. */
const ASK_KEY = askOneKeyLabel()

/**
 * The bell's own state.
 *
 * ── IT DOES NOT POLL, AND THAT IS A CHOICE ──────────────────────────────────
 *
 * Loaded once on mount and re-read when the panel is opened. An interval would
 * put a request per tab per minute against a database that is about to hold
 * ~900 people, to shave minutes off noticing a flag that a two-person rota
 * checks a few times a day anyway. Realtime is the right answer when it is
 * worth having, and it is a subscription, not a timer.
 *
 * OPS-28 is the other reason to be careful here: the hub already had one bug
 * where something re-ran on every tab focus and blanked the screen. Anything
 * added to this hook should be checked against that.
 */
function useNotifications() {
  const [items, setItems] = useState<Notification[]>([])
  const [open, setOpen] = useState(false)
  // 332. New (unread only) or All (unread first). Remembered per browser.
  const [view, setViewState] = useState<BellView>(readBellView)

  useEffect(() => {
    let live = true
    loadNotifications(view === 'new', BELL_LIMIT)
      .then((n) => live && setItems(n))
      // Silent. A person with no notifications and a person whose bell errored
      // both see an empty bell, and an alert box on page load for a failure in
      // a secondary control is worse than the failure.
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [view])

  async function refresh() {
    try {
      setItems(await loadNotifications(view === 'new', BELL_LIMIT))
    } catch {
      /* see above */
    }
  }

  function setView(next: BellView) {
    saveBellView(next)
    setViewState(next)
  }

  /**
   * 332. The mirror of markRead. A row marked read in New stays on screen,
   * dimmed, until the panel is next opened, so this is also the undo for a
   * mis-tap.
   */
  async function markUnread(id: string) {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, readAt: null } : n)))
    try {
      await markNotificationUnread(id)
    } catch {
      /* see markRead */
    }
  }

  async function markRead(id: string) {
    // Optimistic, unlike the moderation queue, and for the opposite reason:
    // read_at is a courtesy flag on the reader's own row with nothing
    // depending on it, so a failed write costs a dot reappearing on reload.
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)))
    await markNotificationRead(id)
  }

  /**
   * 125 (HLP-23). Every unread row, one press. Optimistic like markRead, and
   * for the same reason; a failure costs the dot coming back on reload.
   */
  async function markAllRead() {
    const now = new Date().toISOString()
    setItems((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: now })))
    try {
      await markAllNotificationsRead()
    } catch {
      /* see above */
    }
  }

  return {
    items,
    unread: items.filter((n) => !n.readAt).length,
    open,
    setOpen: (next: boolean) => {
      setOpen(next)
      if (next) void refresh()
    },
    markRead,
    markUnread,
    markAllRead,
    view,
    setView,
  }
}

/**
 * The hub frame: rail and topbar above 820px, topbar and a fixed bottom bar
 * below it, content column throughout.
 *
 * Three things here are deliberate and will look like omissions:
 *
 * 1. EVERY NAV ITEM GOES SOMEWHERE. There is no `ready` flag and no way to
 *    express a disabled item, which is SHL-17 enforced rather than promised —
 *    see the note on NAV below.
 *
 * 2. There is no tenant picker in the topbar yet. Rule 10 says one context
 *    picker — tenant — and everything else is a filter, so when it arrives it
 *    goes here and nowhere else. It is absent rather than stubbed because
 *    ConeTec is the only seeded tenant; a picker with one entry teaches the
 *    wrong thing about what the control is for.
 *
 * 3. There is no user block at the foot of the rail and no Options button in
 *    the topbar. Both folded into the identity menu under the initials —
 *    SHL-19, and the reasoning is in IdentityMenu.tsx.
 */

/**
 * How a SCREEN opens the drawer — WIK-19.
 *
 * The shell owns the drawer's state because the two triggers it renders must
 * open the same conversation. #/wiki needs a third trigger (its own teal
 * control, where the ask box used to be), and threading a callback down
 * through every route to every screen that might ever want one is how a prop
 * ends up on four components that ignore it.
 *
 * The default is a NO-OP rather than a throw. A component rendered outside the
 * shell — which is most of what the web suite mounts — should render, not
 * explode, and the control it draws is inert in a test either way. A throw
 * here would make `useAskOne` a landmine under every future assertion.
 */
const AskOneContext = createContext<OpenAsk>(() => {})

/**
 * Opens the one Ask One panel. It takes no intent: S2 (31 Oct, HLP-44) removed
 * the intent a caller could declare on the way in (HLP-16's shortcut), because
 * Ask One no longer routes. A caller that wants a PERSON links to #/help
 * (lib/helpLink.ts) instead - the welcome's "Tell us something" card does.
 *
 * S5 (HLP-47): it may take a PAGE CONTEXT - a finer chip than the route's, and
 * its own starters - which a guide page uses to say which guide and section
 * the person is reading. The chip still reaches One only as words (S4), and
 * the next plain open clears it.
 */
export type AskContext = { page: { key: string; title: string }; starters: readonly string[] }
export type OpenAsk = (context?: AskContext) => void

/**
 * Opens the one Ask One drawer. Safe to call anywhere; a no-op outside the shell.
 *
 * ── "OUTSIDE THE SHELL" INCLUDES THE BODY OF THE ROUTE THAT RENDERS IT ───────
 *
 * A route renders `<AppShell>` from its own body, so a hook called in that body
 * sits ABOVE the provider and gets the default — the no-op. That is exactly
 * how #/wiki's Ask One button did nothing from 17 Sep to 22 Sep with every
 * assertion green: the suite checked that the button called this hook, and it
 * did. Call it from a component rendered as a CHILD of the shell, never from
 * the route function itself.
 */
export function useAskOne(): OpenAsk {
  return useContext(AskOneContext)
}

/* The lanes context lives in lib/openLanes.ts so a component can read it
   without importing this file (and the SVG this file imports). */

type NavItem = {
  label: string
  /**
   * 4 Oct. REQUIRED, because the collapsed rail shows nothing else. An item
   * without a glyph would be an empty square at 64px with its name only in a
   * tooltip — a link you can find by hovering, which is no link at all.
   * The three Engagement items reuse the TabBar's glyphs: one picture per
   * destination at every width (rule 11, applied to a drawing).
   */
  icon: (p: { size?: number }) => React.ReactElement
  badge?: number
  route: string
  /** Inside Administration (4 Oct): each item carries its own gate — see NAV. */
  needsAdmin?: boolean
  needsShop?: boolean
  needsModerator?: boolean
  /** stars.admin — Star One admin (9 Oct, moved in from Star One's own tabs). */
  needsStarsAdmin?: boolean
  /** SHL-33. content.publish unscoped — Notices, which left the hub. */
  needsNotices?: boolean
  /** 196. audit.read — the audit log (tenant_admin only). */
  needsAudit?: boolean
  /** 145. photo.room — the photo room, under Administration with its own gate. */
  needsPhotoRoom?: boolean
  /** 126. Inside the Support group: the inbox needs a LANE, Ask One needs analytics.read. */
  needsLane?: boolean
  needsAnalytics?: boolean
  /**
   * A sub-nav (David, 10 Oct: "photo room … on the same main nav as contest
   * as a sub nav, main nav can be Photos"). Each child carries its own gate
   * like any item; the list is drawn only when MORE THAN ONE child survives
   * the gates — a lone "Contests" under "Photos" would be the same link
   * twice. The parent is marked active when any child is `current`. Hidden
   * on the collapsed rail, where the parent's glyph is the way in and the
   * page's own tabs (PhotosTabs) carry the sub-nav.
   */
  children?: NavItem[]
}
type NavGroup = {
  heading: string
  items: NavItem[]
  /** Rendered when the viewer holds admin.roles, can reach the shop, holds
   *  content.moderate (4 Oct — Moderation moved in from its own group) OR holds
   *  stars.admin (9 Oct — Star One admin moved in from Star One's tabs).
   *  Opening the group reveals nothing by itself: every item carries its own
   *  gate. */
  needsAnyAdmin?: boolean
  /** Rendered when the viewer holds at least one `support.*` role (085). Not a
   *  permission test: all three lanes carry the SAME permission
   *  (`support.answer`), so `can(me, …)` cannot tell them apart and would open
   *  this for somebody who holds none of them the day a fourth role gains it. */
  needsSupport?: boolean
}

/**
 * ── THE NINE "SOON" ITEMS ARE GONE (SHL-17) ─────────────────────────────────
 *
 * Eight, in fact — the issue and the survey both say nine and the NAV they were
 * counting held eight. Recorded rather than quietly corrected, because the
 * number appears in the tracker and in CLAUDE.md.
 *
 * They were: My Dashboard, Brand Assets, and the whole of Operations
 * (Scheduling, Projects, Scope & billing, Timesheets) and Commercial (CRM &
 * pipeline, Client portal). Rendered as disabled buttons with a "soon" marker
 * on the argument that a nav full of live-looking links to nothing is how a
 * prototype gets mistaken for a build.
 *
 * That argument was right about the links and wrong about the remedy. Measured
 * at 375px on 16 Sep: the rail was 1,932px wide, 1,254px of it those items —
 * so reaching Moderation meant scrolling horizontally past nine controls
 * nobody can press, and hub.css's own reason for preferring a rail to a
 * hamburger ("one tap to any section") had stopped being true. Deleting them
 * takes an employee's rail to exactly 375px with no scroll, which is what
 * makes SHL-18's bottom bar a small change rather than a rewrite.
 *
 * The roadmap they were signalling is not a launch commitment and now has no
 * home on the screen, which is David's call, 16 Sep: the Wave 2–4 signal lives
 * in his notes. Somebody will notice they are gone — that is the answer.
 *
 * `ready` went with them. A flag that is true for every item is a mechanism
 * with no user, and keeping one would leave the door open to the next stub. The
 * rule now has nowhere to hide: if it is in this list, it works.
 */
/** 310. The Surveys page; the NAV entry spells it out for the help scan. */
const SURVEYS_ROUTE = 'surveys'
/** 337. Help; the NAV entry (under You) spells it out for the help scan, and
 *  the identity row names it through this so the admin-home test, which reads
 *  every route after the Support heading, does not count it as admin. */
const HELP_ROUTE = 'help'

const NAV: NavGroup[] = [
  {
    heading: 'Engagement',
    items: [
      { label: 'Engagement Hub', route: '', icon: HomeIcon },
      // Live as of 8 Sep. It had been a "soon" item since the shell was built
      // while WIK-2 sat on the board marked done with nothing behind it — no
      // search function, no client code. 031 built the search and 032 the
      // intake; this is the link to them.
      { label: 'Wiki One', route: 'wiki', icon: QuestionIcon },
      // Live 14 Sep. The fifth route, and the one that ended App.tsx's router
      // argument — OPS-35. Everyone sees it: giving is not a permission,
      // every member has an allowance, and there is nothing role-dependent
      // behind it.
      { label: 'Star One', route: 'stars', icon: StarIcon },
      // 147 (CMP-6). Everybody votes, so nobody is gated. On a phone the rail
      // is gone (SHL-18): the hub's contest card and the bell are the way in.
      // 10 Oct (David): "Photos", with the contests and the gallery (145's
      // photo room, renamed and moved out of Administration) under it. The
      // gallery keeps its own gate, photo.room.
      {
        label: 'Photos',
        route: 'contests',
        icon: CameraIcon,
        children: [
          { label: 'Photo contests', route: 'contests', icon: TrophyIcon },
          { label: 'Gallery', route: 'photos/gallery', needsPhotoRoom: true, icon: ImageIcon },
        ],
      },
      // 310 (David, 25 Oct). Everybody: the page lists the surveys you were
      // asked, and a Survey manager makes new ones from it. On a phone the
      // identity menu carries it (below), and the QR code is the usual way in.
      { label: 'Surveys', route: 'surveys', icon: SurveyIcon },
    ],
  },
  // 337 (HLP-43). You: what is yours. Help is ungated - everybody may reach a
  // person, and the tickets you sent are yours whatever you hold. Before
  // Support on purpose: the admin home reads every route after that heading
  // as an admin page, and Help is not one.
  {
    heading: 'You',
    items: [{ label: 'Help', route: 'help', icon: HelpIcon }],
  },
  // HLP-3. Its own group: answering a private question put to your lane is
  // neither administering people nor moderating a colleague's speech, and 085
  // gave the three support roles no `content.publish`, no `audience.build`
  // and no `content.moderate` deliberately. Somebody may hold this and
  // nothing else.
  //
  // ONE ITEM FOR ALL THREE LANES, not one per lane. `support_inbox()` with no
  // argument returns every lane you hold, and a lane you do not hold is a
  // REFUSAL rather than an empty list — so a nav item per lane would be three
  // links, two of which error, for the two people who hold one lane each.
  //
  // 126 (HLP-35): the group also opens for analytics.read, for ONE item — Ask
  // One on the desk. Each item carries its own gate, the Administration
  // group's shape: a comms admin with no lane gets Ask One and no inbox, a
  // lane holder gets the inbox and no Ask One, and nobody gets a link to a
  // screen that would refuse them.
  //
  // Ask One's glyph is a chart, NOT the ConeTec mark: the desk is analytics
  // about One's answers, a human screen, and the mark is spent on "you are
  // about to ask a machine" (icons.tsx).
  {
    heading: 'Support',
    needsSupport: true,
    items: [
      { label: 'Support inbox', route: 'support', needsLane: true, icon: InboxIcon },
      { label: 'Ask One', route: 'support/ask-one', needsAnalytics: true, icon: ChartIcon },
      // 131: One Question's answers, for Communications — same gate as Ask One.
      { label: 'One Question', route: 'support/one-question', needsAnalytics: true, icon: PencilIcon },
    ],
  },
  // Hiding it is presentation: every function behind it is SECURITY DEFINER
  // and checks the permission itself, which is the real boundary. It sits last
  // because administering people is not what anyone opens the hub to do.
  //
  // THREE ITEMS, THREE DIFFERENT GATES, ONE GROUP. The group shows if ANY
  // applies, and each item carries its own test — because a stock manager in
  // Lima holds no admin.roles and must still reach their shelves, an
  // administrator who runs no room has no business being offered a counting
  // screen they cannot save, and a moderator need not be an administrator.
  //
  // MODERATION MOVED IN ON 4 OCT (David). It had its own "Review" group
  // because content.moderate and admin.roles are different jobs — 021 split
  // `moderator` out of `comms_admin` and 024 refused to make content.moderate
  // scopable. That is still true, and it is why Moderation carries its OWN
  // gate here rather than inheriting the group's: a moderator who is not an
  // administrator sees Administration with one row in it, and no roster.
  {
    heading: 'Administration',
    needsAnyAdmin: true,
    items: [
      // SHL-33 (David, 10 Oct). The admin home: a page of cards, one per
      // destination below and in Support. No gate of its own — the group's
      // gate is its gate, and the identity menu's single "Admin" row opens it.
      { label: 'Admin home', route: ADMIN_HOME_ROUTE, icon: SlidersIcon },
      // SHL-33: was #/admin; #/admin is the admin home now.
      { label: 'People and roles', route: 'admin/people', needsAdmin: true, icon: PeopleIcon },
      // SHL-33: Manage notices left the hub ("they don't need to be in a view
      // on hub", David 10 Oct). Its own page, with the live strip as preview.
      { label: 'Notices', route: 'admin/notices', needsNotices: true, icon: InfoIcon },
      // needsShop is "shop.admin OR runs a room" — see lib/shop.ts. It cannot
      // be a permission check: 064 made the room managers a membership list
      // rather than a role, deliberately.
      { label: 'Shop and stock', route: 'admin/shop', needsShop: true, icon: ShopIcon },
      // 9 Oct (David). Was a third tab on Star One, #/stars/admin, beside the
      // Wall and the Store that everybody opens Star One for. Setting what an
      // activity pays and tidying endorsement words is administration; it
      // carries its own gate, so a Star One admin who holds nothing else sees
      // this group with one row in it. Beside the shop because the stars it
      // sets are what the store spends.
      { label: 'Star One admin', route: 'admin/stars', needsStarsAdmin: true, icon: StarIcon },
      // 145's photo room moved to Photos → Gallery on 10 Oct (David).
      { label: 'Moderation', route: 'moderation', needsModerator: true, icon: FlagIcon },
      // 196 (ACC-8; David, 13 Oct). Last: reading what was done is the least
      // frequent job here. audit.read is tenant_admin's alone - NOT admin.roles,
      // which hr_admin also holds - so it carries its own gate.
      // 301 (David, 19 Oct). How the hub is used - counts only, for whole-
      // company analytics.read (usage_dashboard() refuses a scoped holder).
      // Before the audit log: reading the numbers is the more frequent job.
      { label: 'Usage', route: 'admin/usage', needsAnalytics: true, icon: PulseIcon },
      { label: 'Audit log', route: 'admin/audit', needsAudit: true, icon: HistoryIcon },
    ],
  },
]

/**
 * THE RAIL'S COLLAPSED STATE — 4 Oct.
 *
 * A per-person, per-browser preference, so localStorage and nothing heavier:
 * it is a convenience, nobody else needs to see it, and a lost value costs one
 * click. Every touch is in try/catch — a private window or blocked site data
 * makes the accessor THROW (see lib/notices.ts), and a nav that fails to render
 * over a remembered width is the wrong trade. `typeof` guards renderToString.
 */
const RAIL_KEY = 'oneconetec.rail'
function readRailCollapsed(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(RAIL_KEY) === 'collapsed'
  } catch {
    return false
  }
}
function writeRailCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(RAIL_KEY, collapsed ? 'collapsed' : 'expanded')
  } catch {
    /* see above */
  }
}

export function AppShell({
  current = 'Engagement Hub',
  eyebrow,
  canAdmin = false,
  canShop = false,
  canModerate = false,
  canStarsAdmin = false,
  canPhotoRoom = false,
  canAnalytics = false,
  canNotices = false,
  canAudit = false,
  supportLanes,
  queueCount,
  userName,
  userInitials,
  userRole,
  onSignOut,
  locale,
  modes = [],
  children,
}: {
  current?: string
  /**
   * The line above the title. Defaults to the NAV group `current` sits in.
   *
   * It exists for the screens that are NOT nav destinations — #/profile is the
   * first — because the lookup's `?? 'Engagement'` fallback was written for a
   * stale bookmark and reads as a wrong answer rather than a default when a
   * real screen lands outside NAV.
   */
  eyebrow?: string
  /** Whether People and roles is rendered. Presentation only. */
  canAdmin?: boolean
  /**
   * Whether Shop and stock is rendered — shop.admin, or running a room.
   * Presentation only; every function behind it re-checks for itself.
   */
  canShop?: boolean
  /** Whether Moderation is rendered (under Administration since 4 Oct).
   *  Presentation only — the queue function checks content.moderate in its
   *  own WHERE clause. */
  canModerate?: boolean
  /** Whether Star One admin is rendered (stars.admin; under Administration
   *  since 9 Oct). Presentation only — every read and write behind it checks
   *  the permission itself. */
  canStarsAdmin?: boolean
  /** 145. Whether the gallery (the photo room) is rendered under Photos
   *  (photo.room). Presentation only — photo_room() and
   *  record_photo_download() refuse without it. */
  canPhotoRoom?: boolean
  /** 126. analytics.read — opens Ask One on the desk (comms, the tenant admin). */
  canAnalytics?: boolean
  /** SHL-33. content.publish — Notices under Administration. Presentation
   *  only; create_notice() and the rest check it themselves. */
  canNotices?: boolean
  /** 196. audit.read — the Audit log under Administration. Presentation only;
   *  audit_log() and its two helpers refuse without it. */
  canAudit?: boolean
  /**
   * The support lanes THIS PERSON answers — `myLanes(me)` from lib/support.
   *
   * Two lists, and they are not the same one, which is why only this is a prop.
   * It gates the Support group in the nav: which lanes are MINE.
   *
   * The DOORS in the drawer are gated by the other one — which lanes ANYBODY
   * holds — and that comes from `support_lanes()`, loaded inside this component
   * because the answer is the same on every screen and a per-route fetch would
   * be four copies of one question. Somebody with no lane of their own still
   * needs the doors; somebody with a lane still must not be offered a door to
   * an unstaffed one.
   */
  supportLanes?: readonly SupportLane[]
  /**
   * Items waiting in moderation_queue(), as the Moderation screen holds them.
   * 325: ONLY that screen passes it, so its badge matches the list on the
   * page the moment an item is resolved. Everywhere else the shell's own
   * `waiting_for_me()` read supplies the count (lib/waiting.ts).
   */
  queueCount?: number
  userName: string
  userInitials: string
  userRole: string
  onSignOut: () => void
  /**
   * The reader's locale, handed to the drawer and on to both corpora, and to
   * the identity menu's language picker (SHL-25).
   *
   * EVERY ROUTE PASSES IT AS OF 24 Sep. Before that only Support and
   * SupportThread did, so the drawer on the Home screen told Ask One "en-CA"
   * for everybody — invisible while every person was en-CA, and wrong for 105
   * people the moment 103 gave Peru and Chile es-CL. The suite now counts the
   * callers against the passes.
   *
   * Still optional because a missing locale is a
   * degraded answer rather than a broken screen — ask-one defaults to en-CA
   * and 084 made help_corpus fall back per entry, so the worst case is English
   * guide text for a Spanish speaker rather than the empty corpus that bug
   * produced.
   */
  locale?: string
  /**
   * The modes this screen offers, for the identity menu. Empty for almost
   * everybody, which is the point — see IdentityMenu.
   *
   * The shell renders them and knows nothing about them. A `canModerate`-shaped
   * prop per mode would put the meaning of each one in here, and the shell
   * would then be a second place that knows what moderation is.
   */
  modes?: ModeOption[]
  children: React.ReactNode
}) {
  const navigate = useNavigate()
  const lang = useHubLang()
  const t = SHELL[lang]
  const { canInstall, promptInstall } = useInstallPrompt()

  // Read once, in the initializer: the preference changes only by the toggle
  // below, so there is nothing to re-read on focus (OPS-28).
  const [railCollapsed, setRailCollapsed] = useState(readRailCollapsed)
  function toggleRail() {
    setRailCollapsed((was) => {
      writeRailCollapsed(!was)
      return !was
    })
  }
  const onInstall = promptInstall

  /**
   * WHICH PRIVATE LANES ANYBODY HOLDS — HLP-3, for the drawer's doors.
   *
   * `undefined` until it lands, which `intentRouting` treats as CLOSED. That
   * is the whole reason it starts undefined rather than `[]`: both mean "no
   * door", but a door rendered optimistically during the fetch is a door that
   * reaches nobody for as long as the fetch takes, and the person cannot tell
   * the difference from the outside.
   *
   * Silent on failure, like the bell above and for the same reason: a private
   * door missing is the SAFE degradation — the community door still works, the
   * HR copy is unaffected, and an alert box on page load for a secondary
   * control is worse than the thing it reports.
   *
   * Once, on mount. `support_lanes()` answers a question about role grants,
   * which change by migration or by an admin screen, not by the minute — and
   * OPS-28 is the standing warning about anything here re-running on focus.
   */
  const [openLanes, setOpenLanes] = useState<readonly SupportLane[] | undefined>(undefined)
  useEffect(() => {
    let live = true
    loadSupportLanes()
      .then((rows) => live && setOpenLanes(rows.map((r) => r.lane)))
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  /**
   * ONE DRAWER, TWO TRIGGERS — HLP-12.
   *
   * The state lives here rather than in either trigger, because the topbar
   * button and the bottom-right bubble must open the SAME conversation. Two
   * components each holding their own messages would be two Ask Ones with one
   * name, which is the thing the old not-a-chat-drawer comment refused and the
   * thing WIK-19 is the condition against.
   *
   * It is also why the drawer is rendered by the shell and not by a route: it
   * opens OVER whatever the person was doing, on every screen, without
   * navigating.
   */
  // 1 Nov: open or not is the session provider's (AskOneSession.tsx), above
  // the routes, so a link in an answer that changes the route does not close
  // a docked panel - every route renders its own AppShell.
  const { open: askOpen, setOpen: setAskOpen } = useAskOneSession()
  const [askContext, setAskContext] = useState<AskContext | null>(null)
  const openAsk: OpenAsk = (context) => {
    setAskContext(context ?? null)
    setAskOpen(true)
  }

  /**
   * S2 (HLP-44). THE PAGE CHIP AND THE STARTERS. The page is the route matched
   * against HELP_COVERAGE (lib/askOnePage.ts) and named with the shell's own
   * `current`; the starters are that page's corpus questions, read ONCE, on
   * the panel's first opening, from help_corpus in the reader's locale. The
   * panel takes both as props - it loads nothing itself, so the suite can
   * render it.
   */
  const location = useLocation()
  const pageKey = pageKeyFor(location.pathname)
  // A guide's context belongs to the page it was given on (S5).
  useEffect(() => setAskContext(null), [location.pathname])

  /**
   * THE GUIDE SHEET (HLP-49). A "How … works" link (guides/GuideLink.tsx) puts
   * ?guide=&section= in the page's query; the shell draws that one section
   * beside the page. From the width where Ask One docks it takes Ask One's
   * slot, non-modal; below, a bottom sheet. THE SHEET AND ASK ONE ARE NEVER
   * BOTH OPEN: a sheet arriving closes Ask One, Ask One opening closes the
   * sheet, and the sheet is drawn only while Ask One is shut. Whether it is
   * docked is the stylesheet's answer (--guidesheet-docked, set in the same
   * 1280px block as the drawer's), so 1280 still exists in one place.
   */
  const sheetTarget = guideSheetTarget(new URLSearchParams(location.search))
  const sheetKey = sheetTarget ? `${sheetTarget.guide}/${sheetTarget.section}` : null
  const [sheetRows, setSheetRows] = useState<GuideRow[] | null>(null)
  const [sheetFailed, setSheetFailed] = useState(false)
  // 353 (HLP-50): read on every page now, not only when a sheet is open - a
  // "How … works" link renders only for a section this read returned
  // (ReadableGuidesContext), so nobody is offered a link to a refusal.
  const readableGuides = useMemo(() => (sheetRows ? readableGuideSections(sheetRows) : null), [sheetRows])
  useEffect(() => {
    if (sheetRows) return
    let live = true
    setSheetFailed(false)
    loadGuides(locale).then(
      (rows) => live && setSheetRows(rows),
      () => live && setSheetFailed(true),
    )
    return () => {
      live = false
    }
  }, [sheetRows, locale])
  const [sheetDocked, setSheetDocked] = useState(false)
  useEffect(() => {
    const read = () =>
      setSheetDocked(getComputedStyle(document.documentElement).getPropertyValue('--guidesheet-docked').trim() === '1')
    read()
    window.addEventListener('resize', read)
    return () => window.removeEventListener('resize', read)
  }, [])
  const lastSheet = useRef<string | null>(null)
  useEffect(() => {
    if (sheetKey && sheetKey !== lastSheet.current) setAskOpen(false)
    lastSheet.current = sheetKey
  }, [sheetKey])
  // Closing goes BACK when the link pushed this entry (GuideLink's state), so
  // Back and × agree; otherwise (a reload, a shared link) it drops the query.
  const closeSheet = () => {
    if ((location.state as { guideSheet?: boolean } | null)?.guideSheet) navigate(-1)
    else navigate({ pathname: location.pathname, search: guideSheetSearch(location.search, null) }, { replace: true })
  }
  useEffect(() => {
    if (askOpen && sheetKey) closeSheet()
  }, [askOpen])
  const sheetShown = !!sheetTarget && !askOpen
  const askPage = pageKey === null ? null : { key: pageKey, title: navLabel(lang, current) }
  const [helpQuestions, setHelpQuestions] = useState<ReadonlyMap<string, string>>(new Map())
  useEffect(() => {
    if (!askOpen || helpQuestions.size > 0) return
    let live = true
    void loadHelpQuestions(locale).then((m) => live && setHelpQuestions(m))
    return () => {
      live = false
    }
  }, [askOpen, locale, helpQuestions.size])

  /**
   * CTRL+K (⌘K on a Mac, ⌘ only) OPENS THE PANEL, and closes it again, from
   * anywhere - text fields included (David, 1 Nov late; it replaced the old Ctrl-and-I key).
   * It is the launcher's key, so it lives with the launcher's state; Escape
   * stays with useDismiss. The rule is lib/askOneKey.ts. Not while a dialog
   * traps focus (aria-modal): AddStock's own Ctrl+K search owns it there.
   */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!isAskOneKey(e)) return
      if (document.querySelector('[aria-modal="true"]')) return
      e.preventDefault()
      setAskOpen((o) => !o)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  /**
   * THE DIGITAL BUSINESS CARD (David, 4 Oct). Owned here for the drawer's
   * reason: the hub's rail card, the identity menu and #/profile must open the
   * SAME dialog, over whatever screen the person is on, without navigating.
   * `null` in the context while it is held, so every trigger renders nothing
   * rather than a button that opens nothing (SHL-8).
   */
  const [cardOpen, setCardOpen] = useState(false)
  const openCard = CARD_RELEASED ? () => setCardOpen(true) : null

  /**
   * THE WELCOME MODAL — SHL-5 / FED-27, copy in content (091).
   *
   * Loaded once on mount, like the lanes above and for the same reasons: it
   * answers a question about a promotion window and a dismissal, neither of
   * which changes by the minute, and OPS-28 is the standing warning about
   * anything here re-running on focus.
   *
   * SILENT ON FAILURE. A welcome that will not load is a missing welcome, never
   * a broken hub — the same posture as the notice strip and the bell. Somebody
   * signing in on 13 October with a flaky connection gets the hub.
   *
   * `reopened` is the identity menu's "Show the welcome again". It is separate
   * from `welcome` because the menu entry must exist even once the notice has
   * been dismissed — which is the whole point of David's ruling 4, that a tour
   * you can only see once is the wrong shape for a product people need
   * explained twice.
   */
  const [welcome, setWelcome] = useState<DbNotice | null>(null)
  const [welcomeOpen, setWelcomeOpen] = useState(false)
  const [reopenable, setReopenable] = useState<DbNotice | null>(null)
  const [welcomeSettled, setWelcomeSettled] = useState(false)

  /**
   * THE TOUR — SHL-34 (David, 10 Oct: "auto once, replay from menu").
   *
   * It starts BY ITSELF when all of these hold, and not before:
   *   - this is the hub (the stops are the hub's — elsewhere it waits for the
   *     next visit, which is also what happens when a welcome card took the
   *     person somewhere else);
   *   - the welcome has answered and is closed, the drawer is shut and the
   *     card is not open — one thing on top at a time;
   *   - and 158 says this person has not finished or skipped it. A FAILED read
   *     counts as seen: a tour on every page load is worse than none.
   * Or when it was asked for from the menu on another page (`requestTour`,
   * which survives the remount the navigation causes).
   *
   * Once per mount. AppShell remounts on every navigation, and lib/tour.ts
   * caches the answer for the page's life, so this is one read per session.
   */
  const [tourOpen, setTourOpen] = useState(false)

  // THE BARS STEP ASIDE WHILE YOU READ, below 820px (David, 12 Oct). Pinned
  // while anything that hangs off them is open: the drawer, the tour, or a
  // menu in the topbar (the bell and the initials both say aria-expanded).
  // See lib/useHideOnScroll.ts, including why this reverses part of SHL-18.
  const chromeHidden = useHideOnScroll(
    () => askOpen || tourOpen || !!document.querySelector('.hubbar [aria-expanded="true"]'),
  )
  const [tourSeen, setTourSeen] = useState<boolean | undefined>(undefined)
  const [tourAsked] = useState(() => (current === 'Engagement Hub' ? takeTourRequest() : false))
  const tourStarted = useRef(false)
  useEffect(() => {
    let live = true
    loadTourSeen()
      .then((seen) => live && setTourSeen(seen))
      .catch(() => live && setTourSeen(true))
    return () => {
      live = false
    }
  }, [])

  /**
   * WHICH DATABASE IS THIS — OPS-46 / ADR 0001.
   *
   * Asked of the DATABASE once on mount, never of the build. `null` until it
   * answers, and the strip renders nothing for null and nothing for 'prod';
   * every other answer, including the read failing, puts the amber strip
   * above the hubbar. The reasoning is in lib/environment.ts.
   */
  const [environment, setEnvironment] = useState<EnvironmentName | null>(null)
  useEffect(() => {
    let live = true
    void loadEnvironmentName().then((n) => live && setEnvironment(n))
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    let live = true
    // Two reads rather than one: the first asks "is there a welcome this person
    // has not dismissed" (open it), the second asks "is there one at all"
    // (offer the menu entry). Deriving the second from the first is impossible
    // — a dismissed notice is absent from it by design.
    loadWelcomeModal(false)
      .then((n) => {
        if (!live) return
        setWelcome(n)
        setWelcomeOpen(n !== null)
      })
      .catch(() => undefined)
      // SHL-34: the tour waits for this answer either way, so it never opens
      // underneath a welcome that is still on its way.
      .finally(() => live && setWelcomeSettled(true))
    loadWelcomeModal(true)
      .then((n) => live && setReopenable(n))
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  /**
   * Closing the modal IS the dismissal, and the write is optimistic.
   *
   * The row leaves `active_notices()` the moment it lands, so there is nothing
   * to reconcile; a failed write means it comes back next sign-in, which is the
   * safe direction for a welcome. Deliberately not surfaced as an error — an
   * alert box about a welcome message is worse than the thing it reports.
   */
  // Whichever notice is on screen — the same expression the modal renders from,
  // rather than a second one that can disagree with it (rule 11 in miniature).
  // Reopening an already-dismissed welcome and closing it again writes the same
  // dismissal twice, which `dismiss_notice` upserts; the alternative is a close
  // that records nothing on a path where it should.
  const shownWelcome = welcome ?? reopenable

  function closeWelcome() {
    setWelcomeOpen(false)
    if (shownWelcome) void dismissNotice(shownWelcome.contentId).catch(() => undefined)
  }
  useEffect(() => {
    if (tourStarted.current || tourOpen) return
    if (current !== 'Engagement Hub' || !welcomeSettled || welcomeOpen || askOpen || cardOpen) return
    if (!tourAsked && tourSeen !== false) return
    // A beat after the hub draws, so the cards it fetches are there to point at.
    const timer = window.setTimeout(() => {
      tourStarted.current = true
      setTourOpen(true)
    }, 700)
    return () => window.clearTimeout(timer)
  }, [current, welcomeSettled, welcomeOpen, askOpen, cardOpen, tourAsked, tourSeen, tourOpen])

  /** Done, Skip and Escape all record it — 158 keeps the first time. */
  function closeTour() {
    setTourOpen(false)
    setTourSeen(true)
    void markTourSeen().catch(() => undefined)
  }

  /** The menu's "Take the tour": here, now; anywhere else, on the hub. */
  function takeTour() {
    if (current === 'Engagement Hub') {
      tourStarted.current = true
      setTourOpen(true)
      return
    }
    requestTour()
    go('')
  }

  // The bell owns its own data. It renders on every screen, so making each
  // route fetch notifications and thread them down as a prop would mean four
  // callers that can each forget.
  const { unread, items, open, setOpen, markRead, markUnread, markAllRead, view, setView } = useNotifications()

  /**
   * 325. WHAT WAITS FOR YOU — every count the shell draws, from one read
   * (lib/waiting.ts). Once per mount (the shell remounts on every navigation),
   * again when a screen calls refreshWaiting() after doing something that
   * changes a count, and again when the bell is opened. No timer: the bell's
   * own reasoning above holds here too.
   *
   * `null` until it lands or if it fails: the badges stay away (an error is not
   * "nothing waiting", but it is not worth an alert either) and the bell falls
   * back to counting the rows it loaded.
   */
  const [waiting, setWaiting] = useState<Waiting | null>(null)
  useEffect(() => {
    let live = true
    const read = () => {
      loadWaiting()
        .then((w) => live && setWaiting(w))
        .catch(() => undefined)
    }
    read()
    const off = onRefreshWaiting(read)
    return () => {
      live = false
      off()
    }
  }, [])
  const counts = waiting ?? NOTHING_WAITING

  // The bell's number. The server's count, not the rows loaded (the panel holds
  // the newest 30, so the 31st unread was invisible to a count of `items`).
  // Marking read moves it at once, like the row's own dot; a failed write puts
  // it back on the next read, which is the bell's existing posture.
  const bellCount = waiting ? waiting.notifications : unread
  function readOne(id: string) {
    const row = items.find((n) => n.id === id)
    if (row && !row.readAt) {
      setWaiting((w) => (w ? { ...w, notifications: Math.max(0, w.notifications - 1) } : w))
    }
    void markRead(id)
  }
  function unreadOne(id: string) {
    const row = items.find((n) => n.id === id)
    if (row && row.readAt) {
      setWaiting((w) => (w ? { ...w, notifications: w.notifications + 1 } : w))
    }
    void markUnread(id)
  }
  function readAll() {
    setWaiting((w) => (w ? { ...w, notifications: 0 } : w))
    void markAllRead()
  }

  // Escape and a click outside close the panel — the same hook the filter
  // pickers use. Until now the only way to close it was pressing the bell
  // again, which on a phone means the panel is covering the target you need.
  // Escape puts focus back on the bell; a click elsewhere does not, because the
  // click has already said where the person wants to be.
  const bellRef = useRef<HTMLDivElement>(null)
  const bellButtonRef = useRef<HTMLButtonElement>(null)
  useDismiss(bellRef, open, (viaKeyboard) => {
    setOpen(false)
    if (viaKeyboard) bellButtonRef.current?.focus()
  })

  // react-router as of 14 Sep (OPS-35). navigate(), not an assignment to
  // window.location.hash: HashRouter would pick that up through hashchange and
  // it would keep working, which is exactly why it has to go — two ways to
  // change the route is two answers to one question, and the imperative one
  // silently stops being reachable the day this becomes BrowserRouter.
  const go = (route: string) => navigate(route ? `/${route}` : '/')

  // 325. Which destinations carry a count is lib/waiting.ts's `waitingFor`;
  // the numbers are the one read above. Moderation keeps one exception: the
  // Moderation screen passes the list it is showing, so resolving an item
  // there moves the badge with the row (the reason this was a prop at all).
  const badgeFor = (route: string) =>
    route === 'moderation' && queueCount !== undefined ? queueCount : waitingFor(counts, route)

  const allowed = (item: NavItem) =>
    // People and roles carries its own admin test because the group
    // opens for a shop manager and a moderator too — without it,
    // appointing somebody to a stock room would hand them the roster.
    (!item.needsAdmin || canAdmin) &&
    (!item.needsShop || canShop) &&
    (!item.needsModerator || canModerate) &&
    (!item.needsStarsAdmin || canStarsAdmin) &&
    (!item.needsPhotoRoom || canPhotoRoom) &&
    (!item.needsLane || (supportLanes?.length ?? 0) > 0) &&
    (!item.needsAnalytics || canAnalytics) &&
    (!item.needsNotices || canNotices) &&
    (!item.needsAudit || canAudit)

  const groups = NAV.filter(
    (group) =>
      (!group.needsSupport || (supportLanes?.length ?? 0) > 0 || canAnalytics) &&
      // Any of the four gates opens Administration; the items inside it are
      // filtered separately, so holding one does not reveal another's screen.
      (!group.needsAnyAdmin || canAdmin || canShop || canModerate || canStarsAdmin || canNotices || canAudit || canAnalytics),
  )
    .map((group) => ({
      ...group,
      items: group.items.filter(allowed).map((item) => ({ ...item, children: item.children?.filter(allowed) })),
    }))
    // A heading over nothing is a dead end with a label. The group gates make
    // this unreachable today; it stays so the next gate cannot make it so.
    .filter((group) => group.items.length > 0)

  /**
   * The gated destinations, for the identity menu.
   *
   * Derived from the SAME filtered list the rail renders rather than gated a
   * second time — rule 11. Two copies of "who may see Moderation" is two
   * answers, and they would disagree exactly where it matters.
   *
   * It matters because SHL-18 hides the rail below 820px: without this, a
   * moderator on a phone has no route to the queue at all.
   */
  /* SHL-33. The Support and Administration destinations, for the admin home
     (through context) and to decide whether the menu has an Admin row at all.
     The admin home is not one of its own cards. */
  const adminGroups: AdminGroup[] = groups
    .filter((g) => g.needsAnyAdmin || g.needsSupport)
    .map((g) => ({
      heading: g.heading,
      items: g.items
        .filter((i) => i.route !== ADMIN_HOME_ROUTE)
        .map((i) => ({ label: i.label, route: i.route, badge: badgeFor(i.route) })),
    }))
    .filter((g) => g.items.length > 0)

  /**
   * The identity menu's destinations. SHL-33 (David, 10 Oct): ONE "Admin"
   * row for everybody who holds a role, opening #/admin — not the eight rows
   * it used to list, which ran off a short window (SUP-1). It carries the sum
   * of what waits (the moderation queue), so a moderator on a phone still
   * sees the count without opening anything.
   *
   * It still MUST exist, for the reason this list always existed: SHL-18 hides
   * the rail below 820px, and without it a moderator on a phone has no route
   * to the queue. The admin home is one tap further, and that is the trade.
   */
  const identityLinks: IdentityLink[] = [
    ...(adminDestinationCount(adminGroups) > 0
      ? [{ label: navLabel(lang, 'Admin'), route: ADMIN_HOME_ROUTE, badge: adminBadge(adminGroups) }]
      : []),
    /* HLP-22. UNGATED, AND THAT IS THE POINT.
     *
     * The sidebar's "Support inbox" is gated on holding a lane, correctly —
     * it is a queue you work. This is the other side of the same screen: the
     * private questions you ASKED, which by definition belong to somebody who
     * holds no lane.
     *
     * It has to live HERE rather than in the rail for two reasons, and the
     * second is the load-bearing one. The rail does not render below 820px at
     * all (SHL-18), so a rail entry would leave every phone user with the bell
     * as their only route — and a thread reachable only from a notification
     * stops being reachable the moment that row is marked read and scrolled
     * past, which is the exact "nowhere to go" fault this issue exists to fix,
     * moved one step later where it is harder to notice. The identity menu
     * renders at every width and costs the tabbar nothing, so SHL-18's 375px
     * arithmetic is untouched — which is why this is not a fourth tab.
     *
     * And it belongs here on its own terms: the menu is who you are and what
     * you may do, and a question you asked is yours.
     *
     * 337 (HLP-43): the row is Help now, and "Your tickets" on #/help is where
     * those questions live (One and Help v2). Same reasons, same count. The
     * rail also carries Help, under You, for wide screens. */
    { label: navLabel(lang, 'Help'), route: HELP_ROUTE, badge: counts.questions },
    // 310. Ungated for the reason Help is: the rail is gone on a
    // phone, and a survey you were sent must be reachable without the QR code.
    // Named through SURVEYS_ROUTE: the admin-home test reads every route
    // literal after the Support heading as an admin page, and this is not one.
    { label: navLabel(lang, 'Surveys'), route: SURVEYS_ROUTE, badge: badgeFor(SURVEYS_ROUTE) },
  ]

  return (
    <AskOneContext.Provider value={openAsk}>
    <OpenLanesContext.Provider value={openLanes}>
    <BusinessCardContext.Provider value={openCard}>
    <AdminDestinationsContext.Provider value={adminGroups}>
    <ReadableGuidesContext.Provider value={readableGuides}>
    <div className={`shell${railCollapsed ? ' shell--rail-collapsed' : ''}${chromeHidden ? ' shell--chrome-hidden' : ''}${askOpen ? ' shell--askopen' : ''}${sheetShown && sheetDocked ? ' shell--sheetopen' : ''}`}>
      <a className="skip" href="#main">
        {t.skip}
      </a>

      {/* COLLAPSIBLE SINCE 4 OCT, to a 64px rail of glyphs. Every label stays in
          the markup and is only VISUALLY hidden when collapsed — the button's
          accessible name is its text, so a screen reader hears "Moderation"
          at either width and there is no aria-label to drift from it. `title`
          is added only when collapsed, where the hover tooltip is the one way
          a sighted mouse user learns which square is which. Below 820px none
          of this renders (SHL-18) and the preference simply waits. */}
      <nav className="sidebar" aria-label={t.sectionsLabel}>
        <div className="sidebar__brand">
          <img src={logo} alt="OneConetec" height={26} />
        </div>

        <div className="sidebar__scroll" id="hub-rail">
          {groups.map((group) => (
            <div className="navgroup" key={group.heading}>
              <h2 className="navgroup__heading">{headingLabel(lang, group.heading)}</h2>
              <ul className="navgroup__list">
                {group.items.map((item) => {
                  const subs = item.children && item.children.length > 1 ? item.children : null
                  const inside = item.label === current || !!item.children?.some((c) => c.label === current)
                  // With its sub-nav drawn, the child carries the highlight and
                  // aria-current; the parent only says the section is open.
                  const active = inside && !subs
                  // 325. A parent with a sub-nav (Photos) carries its children's
                  // counts only where the children are not drawn: the collapsed
                  // rail, or a sub-nav with one child left after the gates.
                  const childCount = (item.children ?? []).reduce((sum, c) => sum + (badgeFor(c.route) ?? 0), 0)
                  const badge =
                    badgeFor(item.route) ?? item.badge ?? (childCount > 0 && (!subs || railCollapsed) ? childCount : undefined)
                  const label = navLabel(lang, item.label)
                  const Icon = item.icon
                  return (
                    <li key={item.label}>
                      <button
                        type="button"
                        className={`navitem${active ? ' navitem--active' : ''}${inside && subs ? ' navitem--open' : ''}`}
                        aria-current={active ? 'page' : undefined}
                        title={railCollapsed ? label : undefined}
                        // SHL-34: a tour stop on a desktop; its phone twin is the tab.
                        data-tour={TOUR_NAV[item.route]}
                        onClick={() => go(item.route)}
                      >
                        <span className="navitem__icon" aria-hidden="true">
                          <Icon size={18} />
                        </span>
                        <span className="navitem__label">{label}</span>
                        {badge !== undefined && badge > 0 && (
                          <span
                            className="navitem__badge"
                            aria-label={t.waiting(badge)}
                          >
                            {badgeText(badge)}
                          </span>
                        )}
                      </button>
                      {subs && (
                        <ul className="navsub" aria-label={label}>
                          {subs.map((c) => {
                            const on = c.label === current
                            const SubIcon = c.icon
                            const subBadge = badgeFor(c.route) ?? 0
                            return (
                              <li key={c.label}>
                                <button
                                  type="button"
                                  className={`navsub__item${on ? ' navsub__item--active' : ''}`}
                                  aria-current={on ? 'page' : undefined}
                                  onClick={() => go(c.route)}
                                >
                                  <span className="navitem__icon" aria-hidden="true">
                                    <SubIcon size={16} />
                                  </span>
                                  <span className="navitem__label">{navLabel(lang, c.label)}</span>
                                  {subBadge > 0 && (
                                    <span className="navitem__badge" aria-label={t.waiting(subBadge)}>
                                      {badgeText(subBadge)}
                                    </span>
                                  )}
                                </button>
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="sidebar__foot">
          <button
            type="button"
            className="railtoggle"
            aria-controls="hub-rail"
            aria-expanded={!railCollapsed}
            title={railCollapsed ? t.expandRail : undefined}
            onClick={toggleRail}
          >
            <span className="railtoggle__icon" aria-hidden="true">
              <ChevronIcon size={16} />
            </span>
            <span className="railtoggle__label">{railCollapsed ? t.expandRail : t.collapseRail}</span>
          </button>
        </div>
      </nav>

      <div className="shell__main">
        <EnvironmentBanner name={environment} />
        <header className="hubbar">
          <div className="hubbar__crumbs">
            <p className="hubbar__eyebrow">
              {headingLabel(
                lang,
                eyebrow ??
                  NAV.find((g) => g.items.some((i) => i.label === current || i.children?.some((c) => c.label === current)))
                    ?.heading ??
                  'Engagement',
              )}
            </p>
            <h1 className="hubbar__title">{navLabel(lang, current)}</h1>
          </div>

          <div className="hubbar__tools">
            {/* SHL-32, 11 Oct (David): the top-bar search box is REMOVED, not
                hidden. It sat disabled on every screen promising "people,
                posts, Wiki One" and did nothing. Searching lives where the
                content is: #/wiki, the Gallery, the pickers. A cross-platform
                search, if it comes, is a new design (results grouped by
                source, Ask One as the fallback through the ONE drawer) and
                not this input switched back on. */}
            {/* Install-to-home-screen. Only shown when the browser has actually
                offered it — Chrome and Edge fire beforeinstallprompt, iOS
                Safari never does, and iOS is where the prompt matters most
                because web push does not work there at all until the site is
                on the home screen (B10). So this button appearing is a bonus,
                not the plan; email stays primary for launch. */}
            {canInstall && (
              <button
                type="button"
                className="iconbtn"
                onClick={onInstall}
                aria-label={t.installLabel}
                title={t.installTitle}
              >
                <InstallIcon />
              </button>
            )}

            {/* SHL-3. Live as of 5 Sep, and it was marked done on the tracker
                for four days before anything read the outbox — the same
                done-on-paper shape as FED-4 and WIK-2.

                in_app needs no sender: 023 marks an in_app row `sent` on
                insert because the table IS the delivery. So this works with
                FND-14's Resend adapter still unbuilt, and it is the only
                channel that will. Push is SHL-4 behind B10; email waits on
                ACC-2. */}
            <div className="bellwrap" ref={bellRef}>
              <button
                type="button"
                ref={bellButtonRef}
                className="iconbtn"
                aria-label={bellCount > 0 ? t.notificationsUnread(bellCount) : t.notifications}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => {
                  // Opening re-reads the rows (useNotifications) and the counts.
                  if (!open) refreshWaiting()
                  setOpen(!open)
                }}
              >
                <BellIcon />
                {/* 325: the number, not a dot (David, 30 Oct). The label above
                    says it in words; this is the picture of it. */}
                {bellCount > 0 && (
                  <span className="cornercount" aria-hidden="true">
                    {badgeText(bellCount)}
                  </span>
                )}
              </button>

              {open && (
                // NOT role="dialog". This file's own CSS comment says it: "it
                // is a dropdown, not a dialog". The role promised a screen
                // reader a modal — focus trapped, page behind inert — and
                // delivered a panel with none of that, which is worse than no
                // role at all. A labelled group is what it actually is.
                <div className="bellpanel" role="group" aria-label={t.notifications}>
                  <div className="bellpanel__head">
                    <h2>{t.notifications}</h2>
                    {/* 332. A FILTER over one list, so chips (rule 18), one
                        of two, pressed state said. New asks the database for
                        unread only; All gets unread first from the database.
                        Nothing here sorts or filters the fetched page. */}
                    <div className="bellpanel__views" role="group" aria-label={t.bellViewLabel}>
                      {(['new', 'all'] as const).map((v) => (
                        <button
                          key={v}
                          type="button"
                          className={`chip chip--sm${view === v ? ' chip--on' : ''}`}
                          aria-pressed={view === v}
                          onClick={() => setView(v)}
                        >
                          {v === 'new' ? t.bellNew : t.bellAll}
                          {v === 'new' && bellCount > 0 && <span className="bellpanel__n">{badgeText(bellCount)}</span>}
                        </button>
                      ))}
                    </div>
                    {(unread > 0 || bellCount > 0) && (
                      <button type="button" className="bellpanel__all" onClick={readAll}>
                        {t.markAllRead}
                      </button>
                    )}
                  </div>
                  {items.length === 0 ? (
                    view === 'new' ? (
                      <p className="empty">
                        {t.nothingNew}{' '}
                        <button type="button" className="bellpanel__all" onClick={() => setView('all')}>
                          {t.showAll}
                        </button>
                      </p>
                    ) : (
                      <p className="empty">{t.nothingYet}</p>
                    )
                  ) : (
                    <ul className="bellpanel__list">
                      {items.map((n) => (
                        <li
                          key={n.id}
                          className={n.readAt ? 'bellpanel__item' : 'bellpanel__item bellpanel__item--unread'}
                        >
                          <button
                            type="button"
                            className="bellpanel__row"
                            onClick={() => {
                              readOne(n.id)
                              // HLP-22. The rule used to be one inline line
                              // testing for `moderation.`, so every other kind
                              // marked itself read and dead-ended — "Someone
                              // commented on your post. Sep 13" was a sentence
                              // with no door behind it. `notificationRoute` is
                              // the one home for that decision now. Since 125
                              // the comment and wiki kinds have a door too
                              // (#/item/:id, #/wiki?open=); a kind it cannot
                              // place still returns null rather than guessing.
                              const to = notificationRoute(n)
                              if (to) {
                                navigate(to)
                                setOpen(false)
                              }
                            }}
                          >
                            <span className="bellpanel__text">
                              {describeNotification(n, lang)}
                            </span>
                            <time className="bellpanel__when" dateTime={n.createdAt}>
                              {new Date(n.createdAt).toLocaleDateString(htmlLangFor(lang), {
                                day: 'numeric',
                                month: 'short',
                              })}
                            </time>
                          </button>
                          {/* 332. Read or unread WITHOUT opening it - a
                              sibling, because a button cannot hold a button.
                              The dot is the picture; the label says the act. */}
                          <button
                            type="button"
                            className="bellpanel__mark"
                            aria-label={n.readAt ? t.markUnread : t.markRead}
                            title={n.readAt ? t.markUnread : t.markRead}
                            onClick={() => (n.readAt ? unreadOne(n.id) : readOne(n.id))}
                          >
                            <span className="bellpanel__dot" aria-hidden="true" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* Rule 12's cousin: a limited page says it is one. Only in
                      New, where the server's count is the same set. */}
                  {view === 'new' && bellCount > items.length && items.length > 0 && (
                    <p className="bellpanel__foot">{t.bellShowing(items.length, bellCount)}</p>
                  )}
                </div>
              )}
            </div>

            {/* The Ask One entry point — the drawer's PHONE trigger.

                TWO TRIGGERS MEANS ONE AT EACH WIDTH, NEVER BOTH AT ONCE
                (HLP-15). This is the launcher below 820px; the bubble at the
                foot of this file is the launcher at or above it, and hub.css
                swaps them in a single media query. It shipped on 18 Sep with
                both visible on a desktop, which is the "two of anything" the
                whole drawer was conditional on — see the paragraph below.

                ── IT IS A CHAT DRAWER NOW, AND THE OLD REASON IS WHY ───────
                This comment said from 8 Sep that a drawer was refused
                deliberately: "a second Ask One surface would be a second place
                to keep the machine-authorship disclosure, the rate limit, the
                published-only corpus rule and the escalation path correct —
                and two surfaces answering one question is how the badge and
                the bell started disagreeing in 029."

                David reversed the surface on 17 Sep (HLP-12) and that
                reasoning is not overturned, because it was never against
                drawers. It was against TWO OF ANYTHING. So the drawer ships on
                the condition the old comment sets: it REPLACES the ask box on
                #/wiki rather than joining it (WIK-19). #/wiki is now a
                knowledge base you search, `OneAnswerCard` MOVED into
                AskOneDrawer.tsx rather than being copied, and there is still
                exactly one place where One lives.

                DO NOT RESTORE THE OLD COMMENT. A session reading it beside
                this code would revert the drawer on the strength of a
                paragraph that no longer describes anything, which is the
                failure OPS-13 is named after: a document saying a thing is so
                is not the thing being so.

                ── ONE ASK ONE, EVERYWHERE ──────────────────────────────────
                This used to be `.btn--ask` — brand blue, with StarIcon, the
                same five-point star the ★ Official pill uses. The Wiki One
                screen's button was `.btn--ai` in teal with a ✦ glyph. Two
                controls, one action, two different colours and two different
                marks, and one of them wore the badge that means "a named human
                approved this" on the control that hands the question to a
                machine.

                Both are now the same button and the same mark. That is not
                tidiness — teal + the mark IS the disclosure that this is an
                AI, and a disclosure that appears on one surface and not the
                other is not a disclosure. It is why the bubble below wears it
                too: the launcher discloses itself before it is pressed. */}
            {/* `askbar` is what makes this ONE OF TWO rather than one of the
                two (HLP-15). It carries no styling of its own — it exists so
                hub.css can hide this instance of `.btn--ai` above 820px in the
                same media query that shows the bubble, and only this instance:
                the class is also the drawer's send button and Wiki One's
                control. There is no matchMedia read here on purpose; a JS
                breakpoint would be a second copy of 820 that can disagree with
                the stylesheet, and an effect never runs under
                `renderToString`. */}
            <button
              type="button"
              className="btn btn--ai askbar"
              onClick={() => openAsk()}
              aria-expanded={askOpen}
              aria-haspopup="dialog"
            >
              <ConeTecMark size={16} />
              Ask One
            </button>

            {/* Last, because the avatar is the one control whose position
                people learn by muscle memory across every product they use.
                It carries Options, the user block and Sign out — SHL-19. */}
            <IdentityMenu
              userName={userName}
              userInitials={userInitials}
              userRole={userRole}
              modes={modes}
              links={identityLinks}
              onNavigate={go}
              onSignOut={onSignOut}
              // Undefined when there is no welcome running, so the row is
              // absent rather than inert — SHL-8's trap.
              onReopenWelcome={shownWelcome ? () => setWelcomeOpen(true) : undefined}
              // SHL-34. On every page, for everybody: the tour has no window.
              onTakeTour={takeTour}
              // Undefined while the card is held — same rule as the welcome.
              onOpenCard={openCard ?? undefined}
              // SHL-25's language picker moved to #/profile on 11 Oct (David).
            />
          </div>
        </header>

        <main className="hub" id="main">
          {children}
        </main>
      </div>

      {/* Below 820px only, by CSS — see TabBar. It is outside `.shell__main`
          because it is fixed to the viewport rather than laid out in the
          column, and nesting it in a scrolling ancestor is how a "fixed" bar
          ends up scrolling on iOS.

          AND IT STANDS DOWN WHILE THE DRAWER IS OPEN (SHL-23). `askOpen` is
          the SAME variable that opens the drawer, not a second copy of it —
          the bar and the drawer cannot disagree about whether the drawer is
          open, which is the whole reason this is a prop and not a `:has()`
          selector or a `matchMedia` read. The visible fault was on iOS with
          the soft keyboard up: two fixed surfaces sharing the bottom edge,
          whose heights only add up while the visual viewport is the layout
          viewport. One surface owns that edge now, so there is nothing left
          to add up. See hub.css `.onedrawer` ≤819px for the rest. */}
      <TabBar current={current} onNavigate={go} hidden={askOpen} badgeFor={badgeFor} />

      {/* REC-57. The "+25 stars" pill — once, here, so every screen that can
          earn shows it. See StarPulse.tsx. */}
      <StarPulse locale={locale} />

      {/* THE BUBBLE — the drawer's second trigger, DESKTOP ONLY (≥820px).
          Hidden by CSS below that, not by JS: a `matchMedia` read here would
          be a second copy of the 820px number that can disagree with the
          stylesheet, and an effect never runs under `renderToString`, so the
          suite would see a launcher that is always absent.

          It is bottom-right and it does NOT appear on a phone, because
          `.tabbar` owns the bottom edge below 820px whenever the drawer is
          CLOSED — --tabbar-h plus the home-indicator inset, the only
          navigation at that width. (It stands down while the drawer is open,
          SHL-23, but that is after the launcher has been pressed and so says
          nothing about where the launcher goes.) A bubble
          floating over it, or a fourth tab beside it, both mean redoing
          SHL-18's 375px arithmetic, which SHL-17 spent eight deleted nav items
          getting right. On a phone the topbar control is the trigger.

          Teal with the mark in a ring: the launcher discloses that it is an AI
          before it is pressed, which is the one thing a persistent floating
          control has to do that a labelled button in a toolbar does not. */}
      {/* S2 (31 Oct): the bubble became the BOTTOM-BAR BUTTON of the v2
          mockup - "+ Ask One" with its key (Ctrl K, or ⌘ K on a Mac) - in the same element and
          the same corner, so it is still ONE launcher per width (HLP-15's
          stylesheet walk is unchanged and still passes). */}
      <button
        type="button"
        className="askbubble"
        onClick={() => openAsk()}
        aria-expanded={askOpen}
        aria-haspopup="dialog"
        aria-keyshortcuts={ASK_KEY.aria}
        title={`Ask One (${ASK_KEY.kbd})`}
      >
        <ConeTecMark size={18} />
        <span className="askbubble__label">Ask One</span>
        <kbd className="askbubble__key">{ASK_KEY.kbd}</kbd>
      </button>

      {/* LAST CHILD OF `.shell`, and that is load-bearing rather than tidy.
          It is not a portal — `createPortal` throws under `renderToString`,
          which would make the one surface carrying the AI disclosure, the
          public-publishing warning and the rate limit the one surface the web
          suite cannot mount. Rendering it last is what keeps it above the rest
          without one. */}
      <AskOneDrawer
        open={askOpen}
        onClose={() => {
          setAskOpen(false)
          setAskContext(null)
        }}
        locale={locale}
        page={askContext?.page ?? askPage}
        starters={(key) => (askContext ? [...askContext.starters] : startersFor(key, helpQuestions))}
      />

      {/* HLP-49. The guide sheet, in the drawer's slot and never with it. */}
      {sheetShown && sheetTarget && (
        <GuideSheet
          state={sheetFailed ? { kind: 'failed' } : resolveGuideSheet(sheetRows, sheetTarget)}
          docked={sheetDocked}
          onClose={closeSheet}
          onAskOne={openAsk}
        />
      )}

      {/* THE WELCOME, LAST AND ABOVE EVERYTHING. Same reason the drawer is
          rendered here rather than by a route and the same reason neither is a
          portal: `createPortal` throws under `renderToString`, and this is the
          one surface every single person is guaranteed to meet.

          It sits AFTER the drawer deliberately. A welcome card can open the
          drawer, and it closes itself on the way — but if both were ever open
          at once the modal is the one that must be on top, because it traps
          focus and the drawer does not. */}
      {/* THE BUSINESS CARD. A <Dialog>, so it portals to <body> and the
          stacking context of whichever trigger opened it does not matter. */}
      {CARD_RELEASED && <BusinessCardDialog open={cardOpen} onClose={() => setCardOpen(false)} />}

      <WelcomeModal
        notice={shownWelcome}
        open={welcomeOpen}
        onClose={closeWelcome}
        onNavigate={(href) => go(href.replace(/^#\//, ''))}
        onOpenDrawer={() => openAsk()}
        onTakeTour={takeTour}
      />

      {/* SHL-34. After the welcome in the markup as on screen: it never opens
          while the welcome is up (see the effect above), and it portals to
          <body> in a browser, so its place here only matters to the suite. */}
      <Tour open={tourOpen} onClose={closeTour} />
    </div>
    </ReadableGuidesContext.Provider>
    </AdminDestinationsContext.Provider>
    </BusinessCardContext.Provider>
    </OpenLanesContext.Provider>
    </AskOneContext.Provider>
  )
}
